<script setup lang="ts">
import { ChevronDown, Minus, Plus } from 'lucide-vue-next';

import { ICON_SIZE_MD, ICON_SIZE_SM } from '@/composables/booking/booking-widget.constants';
import { useUnitsDropdown } from '@/composables/booking/useUnitsDropdown';

import MobileModal from '../MobileModal.vue';

const {
  isMobile,
  isOpen,
  dropdown,
  tempSelectedUnits,
  totalCount,
  summaryText,
  isValid,
  units,
  getUnitLabel,
  getLocalizedDescription,
  hasDescription,
  openDropdown,
  closeDropdown,
  canIncrementUnit,
  incrementUnit,
  decrementUnit,
  applySelection,
} = useUnitsDropdown();
</script>

<template>
  <div ref="dropdown" class="units-dropdown">
    <button
      class="dropdown-button"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      @click="openDropdown"
    >
      <div class="button-content">
        <div class="count-badge">
          {{ totalCount }}
        </div>
        <span class="summary-text">{{ summaryText }}</span>
      </div>
      <ChevronDown :size="ICON_SIZE_MD" :class="{ rotate: isOpen && !isMobile }" aria-hidden="true" />
    </button>

    <!-- Desktop Dropdown -->
    <div v-if="!isMobile && isOpen" class="dropdown-menu">
      <div class="units-list">
        <div
          v-for="unit in units"
          :key="unit.id"
          class="unit-item"
        >
          <div class="unit-info">
            <div class="unit-name">
              {{ getUnitLabel(unit) }}
            </div>
            <div v-if="hasDescription(unit)" class="unit-description">
              {{ getLocalizedDescription(unit) }}
            </div>
          </div>
          <div class="quantity-controls">
            <button
              class="quantity-button"
              :aria-label="$t('booking.decreaseQuantity', { unit: getUnitLabel(unit) })"
              :disabled="(tempSelectedUnits.get(unit.id) || 0) <= 0"
              @click="decrementUnit(unit)"
            >
              <Minus :size="ICON_SIZE_SM" aria-hidden="true" />
            </button>
            <span class="quantity-value">{{ tempSelectedUnits.get(unit.id) || 0 }}</span>
            <button
              class="quantity-button"
              :aria-label="$t('booking.increaseQuantity', { unit: getUnitLabel(unit) })"
              :disabled="!canIncrementUnit(unit)"
              @click="incrementUnit(unit)"
            >
              <Plus :size="ICON_SIZE_SM" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      <button class="apply-button-desktop" :disabled="!isValid" @click="applySelection">
        {{ $t('booking.apply') }}
      </button>
    </div>

    <!-- Mobile Modal -->
    <MobileModal
      v-if="isMobile"
      :is-open="isOpen"
      :title="$t('booking.units')"
      @close="closeDropdown"
    >
      <template #body>
        <div
          v-for="unit in units"
          :key="unit.id"
          class="unit-card"
        >
          <div class="unit-info">
            <div class="unit-name">
              {{ getUnitLabel(unit) }}
            </div>
            <div v-if="hasDescription(unit)" class="unit-description">
              {{ getLocalizedDescription(unit) }}
            </div>
          </div>
          <div class="quantity-controls">
            <button
              class="quantity-button"
              :aria-label="$t('booking.decreaseQuantity', { unit: getUnitLabel(unit) })"
              :disabled="(tempSelectedUnits.get(unit.id) || 0) <= 0"
              @click="decrementUnit(unit)"
            >
              <Minus :size="ICON_SIZE_MD" aria-hidden="true" />
            </button>
            <span class="quantity-value">{{ tempSelectedUnits.get(unit.id) || 0 }}</span>
            <button
              class="quantity-button"
              :aria-label="$t('booking.increaseQuantity', { unit: getUnitLabel(unit) })"
              :disabled="!canIncrementUnit(unit)"
              @click="incrementUnit(unit)"
            >
              <Plus :size="ICON_SIZE_MD" aria-hidden="true" />
            </button>
          </div>
        </div>
      </template>
      <template #footer>
        <button class="apply-button-mobile" :disabled="!isValid" @click="applySelection">
          {{ $t('booking.apply') }}
        </button>
      </template>
    </MobileModal>
  </div>
</template>

<style scoped lang="css">
.units-dropdown {
  position: relative;
  width: 100%;
  min-width: 0;
}

.dropdown-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
  min-height: 74px;
  padding: var(--yb-spacing-sm) var(--yb-spacing-base);
  background-color: var(--yb-color-white);
  color: var(--yb-color-black);
  border: 1px solid var(--yb-color-mediumgray);
  border-radius: var(--yb-radius-sm) 0 0 var(--yb-radius-sm);
  cursor: pointer;
  font-size: var(--yb-text-sm);

  .button-content {
    display: flex;
    align-items: center;
    gap: var(--yb-spacing-sm);
    flex: 1;
    min-width: 0;
  }

  .count-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background-color: var(--yb-color-black);
    color: var(--yb-color-white);
    border-radius: 50%;
    font-weight: var(--yb-text-bold);
    font-size: var(--yb-text-sm);
    flex-shrink: 0;
  }

  .summary-text {
    text-align: left;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.4;
    font-size: var(--yb-text-sm);
    text-overflow: ellipsis;
  }

  svg {
    flex-shrink: 0;
    transition: transform 0.2s ease-out;

    &.rotate {
      transform: rotate(180deg);
    }
  }
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background-color: var(--yb-color-white);
  border-radius: var(--yb-radius-sm);
  box-shadow: var(--yb-color-mediumgray) 0px 4px 8px 0px;
  z-index: 10;
  padding: var(--yb-spacing-sm);
  animation: fadeIn 0.2s ease-out;
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-sm);
}

.units-list {
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-sm);
}

.apply-button-desktop {
  width: 100%;
  padding: var(--yb-spacing-sm);
  margin-top: var(--yb-spacing-xs);
  background-color: var(--yb-color-primary);
  color: var(--yb-color-white);
  border: none;
  border-radius: var(--yb-radius-sm);
  font-size: var(--yb-text-sm);
  font-weight: var(--yb-text-semi-bold);
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
}

.unit-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--yb-spacing-sm);
  border-radius: var(--yb-radius-sm);
}

.unit-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.unit-name {
  font-weight: var(--yb-text-semi-bold);
  color: var(--yb-color-black);
  font-size: var(--yb-text-sm);
  line-height: 1.4;
}

.unit-description {
  font-size: var(--yb-text-xs);
  color: var(--yb-color-darkgray);
  line-height: 1.4;
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: var(--yb-spacing-sm);
}

.quantity-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 2px solid var(--yb-color-primary);
  border-radius: 50%;
  background-color: var(--yb-color-white);
  color: var(--yb-color-primary);
  cursor: pointer;
  transition: all 0.15s ease;

  &:disabled {
    border-color: var(--yb-color-mediumgray);
    color: var(--yb-color-mediumgray);
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    background-color: var(--yb-color-primary);
    color: var(--yb-color-white);
  }
}

.quantity-value {
  min-width: 24px;
  text-align: center;
  font-weight: var(--yb-text-semi-bold);
  font-size: var(--yb-text-base);
}

.unit-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--yb-spacing-base) 0;
  border-bottom: 1px solid var(--yb-color-lightgray);
  gap: var(--yb-spacing-sm);
}

.unit-card:last-child {
  border-bottom: none;
}

.apply-button-mobile {
  width: 100%;
  padding: var(--yb-spacing-base);
  background-color: var(--yb-color-primary);
  color: var(--yb-color-white);
  border: none;
  border-radius: var(--yb-radius-sm);
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-bold);
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    opacity: 0.8;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dropdown-menu {
    animation: none;
  }

  .dropdown-button svg,
  .quantity-button,
  .apply-button-desktop,
  .apply-button-mobile {
    transition: none;
  }
}
</style>
